import { useEffect, useMemo, useState } from "react";

import { Tabs } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { Container, LayoutStandard, Loader } from "components";

import { IRootState } from "config/redux";
import { AuthorityConstants, RouteConstants, ServerConstants, ServiceConstants } from "constant";

import { CreditService, UserService } from "services";
import { ClientResponse, CreditOptionsResponse, CreditResponse, UserResponse } from "types";

import { AppUtils } from "utils";

import { ClientDetails } from "./client-details";
import ClientCredit from "./credit";

import { PreviousCreditRequests } from "./previous-credits/previous-credit-requests";

import classes from "./previous-credits.module.css";

const { ROUTE_ERROR_406, ROUTE_MY_CLIENTS } = RouteConstants;
const {
  REQUEST_USERS_CLIENTS_VIEW_ID,
  REQUEST_CREDIT_VIEW_ID,
  REQUEST_CREDIT_NEW_ID,
  REQUEST_CREDIT_OPTIONS_ID,
  REQUEST_CREDIT_STATUS_CHANGE_ID,
  REQUEST_CREDIT_REQUEST_ID,
  REQUEST_PENDING,
  REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID,
} = ServiceConstants;
const { EXCEPTION_IDENTITY_NOT_FOUND } = ServerConstants;

enum TABS {
  CURRENT_REQUEST = "current-request",
  PREVIOUS_REQUESTS = "previous-requests",
}

const ClientViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const agent = window.location.pathname.startsWith(ROUTE_MY_CLIENTS);
  const clientServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_VIEW_ID],
  );
  const creditServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_VIEW_ID],
  );
  const creditNewServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_NEW_ID],
  );
  const creditOptionsServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_OPTIONS_ID],
  );
  const creditStatusServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_STATUS_CHANGE_ID],
  );
  const creditRequestServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_REQUEST_ID],
  );
  const postponeInstallmentServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID],
  );
  const [client, setClient] = useState<UserResponse | undefined>(undefined);
  const [credit, setCredit] = useState<CreditResponse | undefined>(undefined);
  const [options, setOptions] = useState<CreditOptionsResponse | undefined>(undefined);
  const [unreadMessages, setUnreadMessages] = useState<number | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<string | null>(TABS.CURRENT_REQUEST);
  const [previousRequestSelectedId, setPreviousRequestSelectedId] = useState<number | null>(null);

  useEffect(() => {
    UserService.userClientsOne(id!);
    CreditService.creditOptions();
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      clientServiceObject,
      REQUEST_USERS_CLIENTS_VIEW_ID,
      (val: ClientResponse) => {
        setClient(val.user);
        setCredit(val.credit);
        setUnreadMessages(val.unreadMessages);
      },
      (err) => {
        if (err?.data?.code === EXCEPTION_IDENTITY_NOT_FOUND) {
          navigate(ROUTE_ERROR_406);
        }
      },
    );
    AppUtils.resolveServiceObject(creditStatusServiceObject, REQUEST_CREDIT_STATUS_CHANGE_ID, () =>
      CreditService.creditsOne(credit!.id),
    );
    AppUtils.resolveServiceObject(creditRequestServiceObject, REQUEST_CREDIT_REQUEST_ID, () =>
      CreditService.creditsOne(credit!.id),
    );
    AppUtils.resolveServiceObject(creditServiceObject, REQUEST_CREDIT_VIEW_ID, setCredit);
    AppUtils.resolveServiceObject(
      creditOptionsServiceObject,
      REQUEST_CREDIT_OPTIONS_ID,
      setOptions,
    );
    AppUtils.resolveServiceObject(creditNewServiceObject, REQUEST_CREDIT_NEW_ID, setCredit);
    AppUtils.resolveServiceObject(
      postponeInstallmentServiceObject,
      REQUEST_CREDIT_POSTPONE_INSTALLMENT_ID,
      (data) => {
        setCredit((old) => ({ ...old!, postponedInstallments: data }));
      },
    );
  }, [
    clientServiceObject,
    creditStatusServiceObject,
    creditServiceObject,
    creditOptionsServiceObject,
    creditNewServiceObject,
    creditRequestServiceObject,
    postponeInstallmentServiceObject,
  ]);

  const tabTitle = useMemo(() => {
    if (!client) {
      return AppUtils.composeTabTitle(t("clients.view.title"));
    }
    return AppUtils.composeTabTitle(t("clients.view.title")) + " - " + client.fullName;
  }, [client]);

  const isLoading = useMemo(
    () =>
      (clientServiceObject && clientServiceObject.status === REQUEST_PENDING) ||
      (creditOptionsServiceObject && creditOptionsServiceObject.status === REQUEST_PENDING),
    [clientServiceObject, creditOptionsServiceObject],
  );

  const handleTabChange = (value: string | null) => {
    if (value === TABS.PREVIOUS_REQUESTS && previousRequestSelectedId !== null) {
      setPreviousRequestSelectedId(null);
    }
    setActiveTab(value);
  };

  const tabs = [
    {
      value: TABS.CURRENT_REQUEST,
      title: t("credit.current"),
      color: "#8AD572",
    },
    {
      value: TABS.PREVIOUS_REQUESTS,
      title: t("credit.previousRequests.title"),
      color: "#E0E2E6",
    },
  ];

  return (
    <LayoutStandard authority={AuthorityConstants.ROLE_CR_R} title={tabTitle}>
      <Container>
        {isLoading || !client ? (
          <Loader />
        ) : (
          <>
            <ClientDetails
              user={client!}
              credit={credit}
              agent={agent}
              unreadMessages={unreadMessages}
            />

            <Tabs value={activeTab} onChange={handleTabChange} variant={"pills"} mt={"lg"}>
              <Tabs.List grow={true}>
                {tabs.map(({ value, title, color }) => (
                  <Tabs.Tab key={value} value={value} className={classes.tabView} color={color}>
                    {title}
                  </Tabs.Tab>
                ))}
              </Tabs.List>

              <Tabs.Panel value={TABS.CURRENT_REQUEST} className={classes.tabContent}>
                <ClientCredit user={client!} credit={credit} options={options} />
              </Tabs.Panel>
              <Tabs.Panel value={TABS.PREVIOUS_REQUESTS} className={classes.tabContent}>
                <PreviousCreditRequests
                  options={options}
                  navigateToCurrentRequest={() => setActiveTab(TABS.CURRENT_REQUEST)}
                  selectedCreditId={previousRequestSelectedId}
                  setSelectedCreditId={setPreviousRequestSelectedId}
                />
              </Tabs.Panel>
            </Tabs>
          </>
        )}
      </Container>
    </LayoutStandard>
  );
};

export default ClientViewPage;
